import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  ApiFormationsEventIdGet200Response,
  ApiH2hGet200Response,
  ApiMatchEventIdGet200ResponseDataMatch,
  ApiMatchEventIdPreviewGet200Response,
  ApiMatchEventIdTimelineGet200ResponseData,
} from '@/api/livescore/generated';
import { IAdsData } from '@/api/ads/interfaces';
import { livescoreClient } from '@/api/livescore/client';
import Head from 'next/head';
import { useApp } from './AppContext';

interface LivescoreMatchContextType {
  match: ApiMatchEventIdGet200ResponseDataMatch;
  ads: IAdsData;
  timeline: ApiMatchEventIdTimelineGet200ResponseData['match_timeline'] | null;
  timelineLoading: boolean;
  formation: ApiFormationsEventIdGet200Response['data']['formations'] | null;
  setFormation: (formation: ApiFormationsEventIdGet200Response['data']['formations'] | null) => void;
  substitutions: ApiFormationsEventIdGet200Response['data']['substitutions'] | null;
  setSubstitutions: (substitutions: ApiFormationsEventIdGet200Response['data']['substitutions'] | null) => void;
  starters: ApiFormationsEventIdGet200Response['data']['starters'] | null;
  setStarters: (starters: ApiFormationsEventIdGet200Response['data']['starters'] | null) => void;
  substitutes: ApiFormationsEventIdGet200Response['data']['substitutes'] | null;
  setSubstitutes: (substitutes: ApiFormationsEventIdGet200Response['data']['substitutes'] | null) => void;
  h2h: ApiH2hGet200Response['data']['h2h'] | null;
  setH2H: (h2h: ApiH2hGet200Response['data']['h2h'] | null) => void;
  matchPreview: ApiMatchEventIdPreviewGet200Response | null;
}

const LivescoreMatchContext = createContext<LivescoreMatchContextType | undefined>(undefined);

interface LivescoreMatchProviderProps {
  children: ReactNode;
  initMatch: ApiMatchEventIdGet200ResponseDataMatch;
  ads: IAdsData;
}

export const PREMATCH_STATUSES = ['not_started', 'postponed', 'suspended', 'delayed', 'cancelled', 'abandoned'];

export const LIVE_STATUSES = ['started', 'live', 'interrupted'];

export const LivescoreMatchProvider: React.FC<LivescoreMatchProviderProps> = ({ children, initMatch, ads }) => {
  const { appendJsonLd } = useApp();
  const [match, setMatch] = useState<ApiMatchEventIdGet200ResponseDataMatch>(initMatch);

  const [timeline, setTimeline] = useState<ApiMatchEventIdTimelineGet200ResponseData['match_timeline'] | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(true);

  const [formation, setFormation] = useState<ApiFormationsEventIdGet200Response['data']['formations'] | null>(null);
  const [substitutions, setSubstitutions] = useState<
    ApiFormationsEventIdGet200Response['data']['substitutions'] | null
  >(null);
  const [starters, setStarters] = useState<ApiFormationsEventIdGet200Response['data']['starters'] | null>(null);
  const [substitutes, setSubstitutes] = useState<ApiFormationsEventIdGet200Response['data']['substitutes'] | null>(
    null,
  );
  const [h2h, setH2H] = useState<ApiH2hGet200Response['data']['h2h'] | null>(null);
  const [matchPreview, setMatchPreview] = useState<ApiMatchEventIdPreviewGet200Response | null>({
    step: 'fetching',
    success: true,
    preview: undefined,
  });

  useEffect(() => {
    if (PREMATCH_STATUSES.includes(initMatch.sport_event_status.status)) return;

    async function getTimeline() {
      const timelineResponse = await livescoreClient.apiMatchEventIdTimelineGet({ eventId: match.sport_event.id });

      if (timelineResponse.data.data.match_timeline) {
        setTimeline(timelineResponse.data.data.match_timeline);
      }

      setTimelineLoading(false);
    }

    getTimeline();
  }, [initMatch]);

  useEffect(() => {
    let liveMatchInterval: NodeJS.Timer | undefined;

    if (LIVE_STATUSES.includes(match.sport_event_status.status)) {
      liveMatchInterval = setInterval(async () => {
        console.log('Fetching live match data');
        const [liveMatchResponse, formationsResponse] = await Promise.all([
            livescoreClient.apiMatchEventIdAllLiveGet({
            eventId: match.sport_event.id,
          }),
          formation ? livescoreClient.apiFormationsEventIdGet({
            eventId: match.sport_event.id,
          }) : Promise.resolve(null)
        ]);

        if (liveMatchResponse.data.data.match) {
          console.log('Fetched live match data successfully');
          setMatch(liveMatchResponse.data.data.match);
          setTimeline(liveMatchResponse.data.data.match_timeline);
          if (!liveMatchResponse.data.data.match_timeline)
            console.log('Timeline is undefined', liveMatchResponse.data.data.match_timeline);
          return;
        }

        if (formationsResponse !== null && formationsResponse.data.data.formations) {
          setFormation(formationsResponse.data.data.formations);
        }

        console.error("Cloudn't get live match data.");
      }, 10000);
    }

    // Fetch Match Preview
    const eventSource = new EventSource(
      `${process.env.LIVESCORE_BACKEND_URL}/api/match/${match.sport_event.id}/preview`,
    );

    eventSource.addEventListener('status', (e) => {
      const data = JSON.parse(e.data);
      setMatchPreview(data);

      if (data.state === 'no_data') {
        eventSource.close();
      }
    });

    eventSource.addEventListener('result', (e) => {
      const data = JSON.parse(e.data);
      setMatchPreview(data);
      eventSource.close();
    });

    eventSource.addEventListener('error', (e) => {
      setMatchPreview({
        step: 'error',
        success: false,
      });
      eventSource.close();
    });

    return () => {
      if (liveMatchInterval) {
        clearInterval(liveMatchInterval);
      }
    };
  }, []);

  useEffect(() => {
    const data: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'SportsEvent',
      name: `${match.sport_event.competitors[0].name} vs ${match.sport_event.competitors[1].name}`,
      sport: 'Soccer',
      startDate: match.sport_event.start_time,
      homeTeam: {
        '@type': 'SportsTeam',
        name: match.sport_event.competitors.find((c) => c.qualifier === 'home')?.name,
      },
      awayTeam: {
        '@type': 'SportsTeam',
        name: match.sport_event.competitors.find((c) => c.qualifier === 'away')?.name,
      },
      location: {}
    };

    if (match.sport_event?.venue) {
      data['location'] = {
        '@type': 'Place',
        name: match.sport_event.venue?.name,
        address: {
          '@type': 'PostalAddress',
          addressLocality: match.sport_event.venue?.city_name,
          addressCountry: match.sport_event.venue?.country_name,
        },
      };
    }

    if (matchPreview?.preview?.preview) {
      data['description'] = matchPreview?.preview?.preview;
    }

    appendJsonLd(data);
  }, [matchPreview?.preview?.preview, match.sport_event.id, matchPreview?.generated_at]);

  return (
    <LivescoreMatchContext.Provider
      value={{
        match,
        ads,
        timeline,
        timelineLoading,
        formation,
        setFormation,
        substitutions,
        setSubstitutions,
        starters,
        setStarters,
        substitutes,
        setSubstitutes,
        h2h,
        setH2H,
        matchPreview,
      }}
    >
      <Head>
         {matchPreview?.generated_at ? <meta name="last-modified" content={matchPreview.generated_at}/> : null}
      </Head>
      {children}
    </LivescoreMatchContext.Provider>
  );
};

export const useLivescoreMatch = () => {
  const context = useContext(LivescoreMatchContext);
  if (!context) {
    throw new Error('useLivescoreMatch must be used within an LivescoreMatchProvider');
  }
  return context;
};
